import { useState } from 'react';
import { spellcheckLyrics } from '../api/lyrics';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// JS \b only understands ASCII, so word boundaries are "not a letter or a combining
// mark" (Tamil vowel signs are marks).
const wordPattern = (words) =>
  new RegExp(`(?<![\\p{L}\\p{M}])(${words.map(escapeRegExp).join('|')})(?![\\p{L}\\p{M}])`, 'gu');

export function replaceWord(text, word, replacement) {
  return text.replace(wordPattern([word]), replacement);
}

function Highlighted({ text, words }) {
  if (words.length === 0) return text;
  const parts = text.split(wordPattern(words));
  const flagged = new Set(words);
  return parts.map((part, i) =>
    flagged.has(part) ? (
      <mark key={i} className="bg-amber-200 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

export default function SpellcheckPanel({ token, text, languageId, excludeLyricsId, onTextChange }) {
  const [findings, setFindings] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');

  const runCheck = async () => {
    setIsChecking(true);
    setError('');
    try {
      const result = await spellcheckLyrics(token, {
        text,
        language_id: Number(languageId),
        exclude_lyrics_id: excludeLyricsId ?? null,
      });
      setFindings(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsChecking(false);
    }
  };

  const accept = (word, suggestion) => {
    onTextChange(replaceWord(text, word, suggestion));
    setFindings((current) => current.filter((f) => f.word !== word));
  };

  const ignore = (word) => setFindings((current) => current.filter((f) => f.word !== word));

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          Compares each word with how it's spelled across the other lyrics in this language.
        </p>
        <button
          type="button"
          onClick={runCheck}
          disabled={isChecking || !text.trim() || !languageId}
          className="shrink-0 rounded-md border border-indigo-600 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
        >
          {isChecking ? 'Checking...' : 'Check spelling'}
        </button>
      </div>

      {error && <p className="mt-2 text-red-600">{error}</p>}

      {findings && findings.length === 0 && !error && (
        <p className="mt-2 text-green-700">No suspected spelling mistakes found.</p>
      )}

      {findings && findings.length > 0 && (
        <div className="mt-3 space-y-3">
          <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded border border-gray-200 bg-white p-2 leading-relaxed text-gray-800">
            <Highlighted text={text} words={findings.map((f) => f.word)} />
          </div>
          <ul className="space-y-2">
            {findings.map((finding) => (
              <li key={finding.word} className="flex flex-wrap items-center gap-2">
                <mark className="bg-amber-200 rounded px-1">{finding.word}</mark>
                <span className="text-gray-400">→</span>
                {finding.suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => accept(finding.word, suggestion)}
                    className="rounded-full bg-indigo-600 px-2.5 py-0.5 text-xs font-medium text-white hover:bg-indigo-700"
                    title={`Replace every "${finding.word}" with "${suggestion}"`}
                  >
                    {suggestion}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => ignore(finding.word)}
                  className="text-xs text-gray-500 hover:underline"
                >
                  Ignore
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            Accepting a suggestion only edits the text above — nothing is published until you Save.
          </p>
        </div>
      )}
    </div>
  );
}
